import { Injectable } from '@angular/core';
import { ApiQuoteResponse, SessionStorage, ApiCoverageGroups, ApiCoverageDetail, DateInformation, ApiTraveler, Country, ApiResponseContainer } from '@bhtp-client-api/base';
import { BhtpCountryService } from '@bhtp-client-api/eligibility';
import { PurchaseData, Traveler, TramsPolicyDescription, ReceiptData, TramsCustomQuote, Address, BillingInformation} from './../models';
import { Constants } from './../settings';
import * as moment from 'moment';
import { Observable, Observer } from 'rxjs';

@Injectable()
export class TramsService {
    private xmlDoc: Document;
    private elements: NodeListOf<Element>;

    constructor(private sessionStorage: SessionStorage, private countryService: BhtpCountryService) { }

    public isTramsSession(): boolean {
        // This cannot use the SessionStorage service, because this function
        // gets assigned to a variable on a configuration object. When the function is called,
        // the service would be undefined
        return (window.sessionStorage['isCustomSession'] == 'true') || false; // tslint:disable-line
    }

    public generateXml(receiptData: ReceiptData): string {
        let xmlString: string = '';

        this.xmlDoc = document.implementation.createDocument(null, 'RESCARD', null);

        this.setNode('DATEFORMAT', 'RESCARD', Constants.DATE_TRAMS_FORMAT);
        this.setNode('RESERVATION', 'RESCARD');
        this.setNode('VENDORCODE', 'RESERVATION', 'BHTP');
        this.setNode('VENDORNAME', 'RESERVATION', 'Berkshire Hathaway Travel Protection');
        this.setNode('RESTYPE', 'RESERVATION', '4');
        this.setNode('RESDATE', 'RESERVATION', moment().format(Constants.DATE_TRAMS_FORMAT));
        this.setNode('CONFIRMATIONNUMBER', 'RESERVATION', receiptData.quoteResponse.policyNumber.toString());
        this.setNode('SOURCEOFBOOKING', 'RESERVATION', 'Live Connect');
        this.setNode('BOOKINGSTATUS', 'RESERVATION', 'CONFIRMED');
        this.setNode('STATUS', 'RESERVATION', 'Paid in Full');
        this.setNode('NOOFUNITS', 'RESERVATION', '1');
        this.setNode('NOOFPAX', 'RESERVATION', '1');
        this.setNode('CURRENCYCODE', 'RESERVATION', 'USD');

        let totalCost = receiptData.totalCost ? receiptData.totalCost.toFixed(2).toString() : '0.00';

        this.setNode('TOTALBASE', 'RESERVATION', totalCost);

        this.setNode('STARTDATE', 'RESERVATION', formatDateInformation(receiptData.quoteResponse.departureDates));
        this.setNode('ENDDATE', 'RESERVATION', formatDateInformation(receiptData.quoteResponse.returnDates));
        this.setNode('PASSENGERLIST', 'RESERVATION');

        let allTravelers: Array<Traveler> = ReceiptData.getAllTravelers(receiptData);

        for (let i: number = 0; i < allTravelers.length; i++) {
            this.setNode('PASSENGER', 'PASSENGERLIST');
            this.setNode('LASTNAME', 'PASSENGER', allTravelers[i].lastName, i);
            this.setNode('FIRSTNAME', 'PASSENGER', allTravelers[i].firstName, i);
            this.setNode('PASSENGERTYPE', 'PASSENGER', 'A', i);
        }

        this.setNode('SERVICEPROVIDERLIST', 'RESERVATION');
        this.setNode('SERVICEPROVIDER', 'SERVICEPROVIDERLIST');
        this.setNode('PROVIDERTYPE', 'SERVICEPROVIDER', '4');
        this.setNode('SERVICEPROVIDERNAME', 'SERVICEPROVIDER', 'Berkshire Hathaway Travel Protection');
        this.setNode('STARTDATE', 'SERVICEPROVIDER', formatDateInformation(receiptData.quoteResponse.departureDates));
        this.setNode('ENDDATE', 'SERVICEPROVIDER', formatDateInformation(receiptData.quoteResponse.returnDates));
        this.setNode('ENDDATE', 'SERVICEPROVIDER', formatDateInformation(receiptData.quoteResponse.returnDates));
        this.setNode('DESCRIPTION', 'SERVICEPROVIDER', 'Full Coverage Insurance');

        let policyDesc: TramsPolicyDescription = this.generatePolicyDescription(receiptData);
        this.setNode('ITINREMARKS', 'SERVICEPROVIDER', this.generatePolicyDescriptionText(policyDesc, receiptData.quoteResponse.coverages));

        let serializer: XMLSerializer = new XMLSerializer();
        xmlString = serializer.serializeToString(this.xmlDoc);

        return xmlString;

        function formatDateInformation(dateInfo: DateInformation): string {
            let formattedDateString: string = null;
            if (dateInfo && dateInfo.localized && dateInfo.localized.dateString) {
                formattedDateString = moment(dateInfo.localized.dateString).format(Constants.DATE_TRAMS_FORMAT);
            }
            return formattedDateString;
        }
    }

    public mapTramsQuote(): Observable<PurchaseData> {
        let customQuote: TramsCustomQuote = this.sessionStorage.getValueOfType<TramsCustomQuote>('customSession');
        if (customQuote) {
            return this.mapTramsCustomQuoteToPurchaseData(customQuote);
        }
        else {
            return Observable.empty();
        }
    }

    private loadCountry(countryIsoCode2: string): Observable<Country> {
        return Observable.create((observer: Observer<Country>) => {
            if (countryIsoCode2) {
                this.countryService.getCountryByIsoCode2(countryIsoCode2)
                    .subscribe(
                        (next: Country) => {
                            nextComplete(next);
                        },
                        (error: Error) => {
                            nextComplete(null);
                        });
            }
            else {
                nextComplete(null);
            }

            function nextComplete(country: Country): void {
                observer.next(country);
                observer.complete();
            }
        });
    }

    private mapTramsCustomQuoteToPurchaseData(customQuote: TramsCustomQuote): Observable<PurchaseData> {
        return this.loadCountry(customQuote.destinationCountry)
            .map((country: Country): PurchaseData => {
                let mappedPurchaseData: PurchaseData = new PurchaseData();

                if (customQuote) {
                    this.mapTravelers(mappedPurchaseData, customQuote.travelers, customQuote.tripCost);

                    mappedPurchaseData.departureDate = customQuote.departureDate;
                    mappedPurchaseData.returnDate = customQuote.returnDate;
                    mappedPurchaseData.depositDate = customQuote.tripDepositDate;
                    mappedPurchaseData.primaryTraveler.address = Address.fromApiAddress(customQuote.billToAddress);

                    mappedPurchaseData.destinationCountry = country;
                    mappedPurchaseData.destinationState = customQuote.destinationState;
                    mappedPurchaseData.destinationCity = customQuote.destinationCity;

                    mappedPurchaseData.airline = customQuote.airline;
                    if (!mappedPurchaseData.airline) {
                        mappedPurchaseData.airlineNotProvided = true;
                    }

                    mappedPurchaseData.tourOperator = customQuote.tourOperator;
                    mappedPurchaseData.carRentalCompany = customQuote.carRentalCompany;
                    mappedPurchaseData.hotel = customQuote.hotel;
                    mappedPurchaseData.cruiseLine = customQuote.cruiseLine;
                    mappedPurchaseData.rentalCompany = customQuote.rentalCompany;

                    mappedPurchaseData.billing = <BillingInformation>Address.fromApiAddress(customQuote.billToAddress);
                }

                return mappedPurchaseData;
            });
    }

    private mapTravelers(purchaseData: PurchaseData, travelers: Array<ApiTraveler>, tripCost: number): void {
        if (purchaseData && travelers && travelers.length > 0) {
            let dividedTripCost: number = 0;
            let remainderTripCost: number = 0;
            let primaryTripCost: number = tripCost;

            if (tripCost) {
                dividedTripCost = Math.floor(tripCost / travelers.length);
                remainderTripCost = tripCost - dividedTripCost * travelers.length;
                primaryTripCost = dividedTripCost + remainderTripCost;
            }

            for (let traveler of travelers) {
                let mappedTraveler: Traveler = Traveler.fromApiTraveler(traveler);

                if (traveler.isPrimary) {
                    mappedTraveler.tripCost = primaryTripCost;
                    purchaseData.primaryTraveler = mappedTraveler;
                }
                else {
                    mappedTraveler.tripCost = dividedTripCost;
                    purchaseData.additionalTravelers.push(mappedTraveler);
                }
            }
        }
    }

    private setNode(node: string, parentNode: string, data: string = null, idx: number = null): void {
        let element: HTMLElement = this.xmlDoc.createElement(node);

        // If the element needs data
        if (data) {
            // Create the element data
            let elementValue: Text = this.xmlDoc.createTextNode(data);
            // Attach value to the element
            element.appendChild(elementValue);
        }

        // Get the current elements in the document
        this.elements = this.xmlDoc.getElementsByTagName(parentNode);

        if (idx) {
            this.elements[idx].appendChild(element);
        }
        else {
            this.elements[0].appendChild(element);
        }
    }

    private generatePolicyDescription(receiptData: ReceiptData): TramsPolicyDescription {
        let policyDesc: TramsPolicyDescription = new TramsPolicyDescription();

        let response: ApiQuoteResponse = receiptData.quoteResponse;

        policyDesc.policyNumber = receiptData.policyNumber.toString();
        policyDesc.packageName = response.packageName;

        if (response.expirationDates) {
            policyDesc.expirationDate = response.expirationDates.utc.fullDateTime;
            policyDesc.localExpirationDate = response.expirationDates.localized.fullDateTime;
            policyDesc.expirationTimezone = response.expirationDates.localOlsonTimeZone;
        }

        if (response.effectiveDates && response.effectiveDates.utc && response.effectiveDates.localized) {
            policyDesc.effectiveDate = response.effectiveDates.utc.fullDateTime;
            policyDesc.localEffectiveDate = response.effectiveDates.localized.fullDateTime;
            policyDesc.effectiveTimezone = response.effectiveDates.localOlsonTimeZone;
        }

        if (response.depositDates && response.depositDates.localized) {
            policyDesc.tripDepositDate = response.depositDates.localized.fullDateTime;
        }

        if (response.departureDates && response.departureDates.localized && response.departureDates.utc) {
            policyDesc.localDepartureDate = response.departureDates.localized.fullDateTime;
            policyDesc.departureDate = response.departureDates.utc.fullDateTime;
        }

        if (response.returnDates && response.returnDates.localized && response.returnDates.utc) {
            policyDesc.localReturnDate = response.returnDates.localized.fullDateTime;
            policyDesc.returnDate = response.returnDates.utc.fullDateTime;
        }

        policyDesc.destinationCity = receiptData.destinationCity;
        policyDesc.destinationState = receiptData.destinationState;

        if (receiptData.destinationCountry) {
            policyDesc.destinationCountry = receiptData.destinationCountry.isoCode2;
        }

        return policyDesc;
    }

    private generatePolicyDescriptionText(policyDesc: TramsPolicyDescription, coverages: ApiCoverageGroups): string {
        let output: string = '';
        let delimiter: string = '|';

        try {
            for (let property in policyDesc) {
                if (policyDesc.hasOwnProperty(property)) {
                    output += property.toUpperCase() + ':' + policyDesc[property] + delimiter;
                }
            }

            output += this.generateCoverageDescriptionText(coverages.included);
            output += this.generateCoverageDescriptionText(coverages.bonus);
            output += this.generateCoverageDescriptionText(coverages.optional);
        }
        catch (error) {
            output = 'There was an issue gathering the reservation details.';
        }

        output += 'If you have any questions regarding this policy, please call BHTP at 844-411-BHTP or email us at assist@bhtp.com';

        return output;
    }

    private generateCoverageDescriptionText(coverages: Array<ApiCoverageDetail>): string {
        let output: string = '';

        if (coverages) {
            for (let coverage of coverages) {
                output += coverage.name.replace('&', 'and') + ' ' + coverage.coverageLimitDisplayText + ' ' + coverage.coverageLimitFormattedFor + '|';
            }
        }

        return output;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/shared/services/trams.service.ts